import React, { forwardRef } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  MAX_VISIBLE_DOTS,
  DOT_CELL_PX,
  DOT_SIZE,
  ACTIVE_DOT_SIZE,
  CAROUSEL_HOVER_MEDIA,
} from './constants'

const getVisibleWindowStart = (
  total: number,
  selectedIndex: number,
): number => {
  if (total <= MAX_VISIBLE_DOTS) return 0
  return Math.min(
    Math.max(0, selectedIndex - (MAX_VISIBLE_DOTS - 2)),
    total - MAX_VISIBLE_DOTS,
  )
}

const StyledNavigationDotsContainer = styled.div<{ $isHovered?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
  pointer-events: none;
  opacity: 0;
  transition: opacity 200ms ease;

  ${CAROUSEL_HOVER_MEDIA} {
    opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  }
`

const StyledDotsViewport = styled.div<{
  $visibleSlots: number
  $cellWidth: number
  $isHovered?: boolean
}>`
  overflow: hidden;
  width: ${({ $visibleSlots, $cellWidth }) => $visibleSlots * $cellWidth}px;
  max-width: 100%;
  margin-inline: auto;
  pointer-events: none;

  ${CAROUSEL_HOVER_MEDIA} {
    pointer-events: ${({ $isHovered }) => ($isHovered ? 'auto' : 'none')};
  }
`

const StyledNavigationDotsTrack = styled.ul<{
  $offsetPx: number
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  margin-bottom: 4px;
  transform: translate3d(${({ $offsetPx }) => $offsetPx}px, 0, 0);
  transition: transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const StyledNavigationDot = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${DOT_CELL_PX}px;
  width: ${DOT_CELL_PX}px;
  flex-shrink: 0;
  transform-origin: center;
  ${s({
    p: 3,
  })}
`

const StyledDot = styled.span`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.2);
  transition: background-color 200ms ease, width 200ms ease, height 200ms ease;

  &.active {
    width: ${ACTIVE_DOT_SIZE}px;
    height: ${ACTIVE_DOT_SIZE}px;
    background-color: rgba(0, 0, 0, 0.5);
  }

  &.trailing {
    transform: scale(0.7);
  }

  &.active.trailing {
    transform: scale(0.9);
  }
`

type NavigationDotsProps = {
  items: number[]
  selectedIndex: number
  isHovered?: boolean
}

export const NavigationDots = forwardRef<HTMLUListElement, NavigationDotsProps>(
  ({ items, selectedIndex, isHovered = false }, ref) => {
    const total = items.length
    const windowStart = getVisibleWindowStart(total, selectedIndex)
    const visibleSlots = Math.min(MAX_VISIBLE_DOTS, Math.max(1, total))
    const offsetPx = total > MAX_VISIBLE_DOTS ? -windowStart * DOT_CELL_PX : 0
    const rightmostVisibleSlideIndex = windowStart + visibleSlots - 1

    if (total === 0) {
      return (
        <StyledNavigationDotsContainer aria-hidden $isHovered={isHovered}>
          <StyledDotsViewport
            data-testid="gift-image-carousel-navigation-dots-viewport"
            $visibleSlots={1}
            $cellWidth={DOT_CELL_PX}
            $isHovered={isHovered}
          >
            <StyledNavigationDotsTrack
              ref={ref}
              data-testid="gift-image-carousel-navigation-dots"
              $offsetPx={0}
            />
          </StyledDotsViewport>
        </StyledNavigationDotsContainer>
      )
    }

    return (
      <StyledNavigationDotsContainer aria-hidden $isHovered={isHovered}>
        <StyledDotsViewport
          data-testid="gift-image-carousel-navigation-dots-viewport"
          $visibleSlots={visibleSlots}
          $cellWidth={DOT_CELL_PX}
          $isHovered={isHovered}
        >
          <StyledNavigationDotsTrack
            ref={ref}
            data-testid="gift-image-carousel-navigation-dots"
            $offsetPx={offsetPx}
          >
            {items.map((snap, slideIndex) => {
              const isActive = slideIndex === selectedIndex
              const isTrailingPeek =
                total > MAX_VISIBLE_DOTS &&
                slideIndex === rightmostVisibleSlideIndex &&
                slideIndex < total - 1
              const dotClassName = [
                isActive ? 'active' : '',
                isTrailingPeek ? 'trailing' : '',
              ]
                .filter(Boolean)
                .join(' ')

              return (
                <StyledNavigationDot key={String(snap)}>
                  <StyledDot aria-hidden className={dotClassName} />
                </StyledNavigationDot>
              )
            })}
          </StyledNavigationDotsTrack>
        </StyledDotsViewport>
      </StyledNavigationDotsContainer>
    )
  },
)

NavigationDots.displayName = 'NavigationDots'
